import { isAnyOf } from '@reduxjs/toolkit';
import { equals } from 'ramda';

import { format, parseISO, startOfDay } from 'date-fns';
import { actions as cartActions } from '../cart/reducer';
import ooeConstants from '../constants';
import { actions as eventDetailsActions } from '../eventDetails/reducer';
import { validateAddress as validateEventDetailsAddress } from '../eventDetails/thunks';
import { getAddressSearchSeed } from '../eventDetails/utils';
import { actions as guestActions } from '../guest/reducer';
import { selectFullName, selectIsCmtCustomerSelected } from '../guest/selectors';
import { initializeGuestAccount } from '../guest/thunks';
import { AppStartListening } from '../listenerMiddleware';
import { State } from '../reducers';
import { createSubmitOrderAnalytics, logEvent } from '../services/analytics';
import { getVariation } from '../services/launchDarkly';
import { selectDestinationNotValidForLocation } from '../shared/selectors';
import { DuplicateOrderErrorPayload, EditOrderErrorPayload } from '../types/order';
import { actions as userActions } from '../user/reducer';
import { selectBypassBusinessRules } from '../user/selectors';
import { DOPErrorMessages } from '../util/customerErrorMessages';
import history from '../util/history';
import { roundNumber, stripPaperGoodsFromSpecialInstructions } from '../util/utils';
import { actions as orderActions } from './reducers';
import { Order } from './schemas';
import {
  selectApartmentSuite,
  selectDeliveryAddressValid,
  selectLastAddressSentForValidation,
  selectOverrideGuestDetails,
} from './selectors';
import {
  autocompleteAddress,
  getOrderDates,
  submitOrder,
  updateOrder,
  validateAddress,
  validateDeliveryRange,
} from './thunks';

const addOrderListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: userActions.updateUserLocation,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(orderActions.loadingDates());
    },
  });

  startListening({
    matcher: isAnyOf(
      cartActions.deleteItem,
      cartActions.removeManyItems,
      cartActions.addToCart,
      cartActions.addModifier,
      cartActions.updateSideItem,
      cartActions.updateDessertItem,
      cartActions.updatePremiumSideItem,
      cartActions.updateQuantity,
      cartActions.updateModifierQuantity,
      orderActions.setDetailsDate,
      orderActions.setSmsConsent,
    ),
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(orderActions.updatingOrder());
    },
  });

  startListening({
    predicate: (action): action is ReturnType<typeof guestActions.setCMTCustomer> =>
      guestActions.setCMTCustomer.match(action) && !action.payload.cfaId,
    effect: (_, listenerApi) => {
      const { getState, dispatch } = listenerApi;
      const state = getState() as State;
      dispatch(orderActions.notReorder());
      if (selectOverrideGuestDetails(state)) {
        dispatch(orderActions.setEditOrderDismissibleMessage(''));
      }
    },
  });

  startListening({
    actionCreator: userActions.updateUserLocation,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(orderActions.setSmsConsent({ smsConsent: null }));
    },
  });

  startListening({
    actionCreator: orderActions.exitEditOrder,
    effect: (action) => {
      const { type } = action;
      logEvent(type);
    },
  });

  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action) => {
      const { type, payload } = action;

      const eventPayload = {
        clientId: payload.order.clientId,
        destination: payload.order.destination,
        status: payload.order.status,
        paymentType: payload.order.payment?.paymentType,
        cfaId: payload.guest.cfaId,
        reorder: payload.reorder ?? false,
      };

      logEvent(type, eventPayload);
    },
  });

  startListening({
    actionCreator: orderActions.changeDestination,
    effect: (action) => {
      const { type, payload } = action;
      logEvent(type, payload);
    },
  });

  startListening({
    matcher: isAnyOf(
      cartActions.addToCart,
      cartActions.addModifier,
      cartActions.updateSideItem,
      cartActions.updateDessertItem,
      cartActions.updatePremiumSideItem,
      cartActions.updateQuantity,
      cartActions.updateModifierQuantity,
      cartActions.deleteItem,
      cartActions.removeManyItems,
      cartActions.removeNonExistentItems,
      cartActions.makePromoFree,
      cartActions.removePromoFree,
      initializeGuestAccount.fulfilled,
      orderActions.setDetailsTime,
      orderActions.setDetailsDate,
      guestActions.retrieveGuestAdditionalInformation,
      validateAddress.fulfilled,
      validateEventDetailsAddress.fulfilled,
      orderActions.setDetailsGuestCount,
      orderActions.updatePricing,
      orderActions.addSecondaryContact,
      orderActions.setSmsConsent,
      orderActions.setOverrideGuestDetails,
    ),
    effect: async (action, listenerApi) => {
      const { dispatch, cancelActiveListeners, delay, extra, getState } = listenerApi;

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      cancelActiveListeners();
      await delay(ooeConstants.DXE_DELAY_TIME);

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }
      const state = getState() as State;

      // Check DOP, update order failure if location does not accept orders for selected destination
      const destinationNotValidForLocation = selectDestinationNotValidForLocation(state);
      if (destinationNotValidForLocation) {
        const message = DOPErrorMessages.DESTINATION_INVALID;
        dispatch(updateOrder.rejected({ name: message, message }, ''));
        return;
      }

      dispatch(updateOrder());
    },
  });

  startListening({
    actionCreator: submitOrder.rejected,
    effect: (action) => {
      const { payload } = action;
      if (payload && typeof payload === 'object' && 'errorType' in payload) {
        const errorPayload = payload as DuplicateOrderErrorPayload | EditOrderErrorPayload;

        switch (errorPayload.errorType) {
          case ooeConstants.ORIGINAL_ORDER_CANCEL_FAILED:
            history.push('/error/edit-failed', { errorPayload });
            break;
          case ooeConstants.ORIGINAL_AND_EDITED_ORDER_CANCEL_FAILED:
            history.push('/error/duplicate-order', { errorPayload });
            break;
          default:
            break;
        }
      }
    },
  });

  startListening({
    actionCreator: orderActions.autocompleteAddress,
    effect: (action, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const lastAddressSentForValidation = selectLastAddressSentForValidation(state);

      // Only dispatch thunk if address is different from last one
      if (!equals(action.payload.address, lastAddressSentForValidation)) {
        dispatch(autocompleteAddress(action.payload));
      }
    },
  });

  startListening({
    actionCreator: validateAddress.fulfilled,
    effect: (_, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const validAddress = selectDeliveryAddressValid(state);

      if (validAddress) {
        dispatch(validateDeliveryRange());
      }
    },
  });

  // Flag-on seam: event-details owns typeahead + validate-on-select, but the order
  // slice still holds the committed delivery address and runs the range check.
  startListening({
    actionCreator: validateEventDetailsAddress.fulfilled,
    effect: (action, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      dispatch(orderActions.applyValidatedDeliveryAddress(action.payload));

      const state = getState() as State;
      if (selectDeliveryAddressValid(state)) {
        dispatch(validateDeliveryRange());
      }
    },
  });

  startListening({
    actionCreator: eventDetailsActions.setAddressSearchValue,
    effect: (_, { dispatch, getState }) => {
      // Editing the search field after an address was accepted must drop the committed
      // delivery address so it no longer counts as valid. The reducer already cleared the
      // selected place id, so a still-valid committed address means it is now stale.
      const state = getState() as State;
      if (selectDeliveryAddressValid(state)) {
        dispatch(orderActions.invalidateValidatedDeliveryAddress());
      }
    },
  });

  startListening({
    actionCreator: orderActions.selectPastDeliveryAddress,
    effect: (action, listenerApi) => {
      const { dispatch } = listenerApi;
      const address = action.payload;
      const { addressToValidate } = getAddressSearchSeed(address);

      dispatch(
        orderActions.updateUserAddressField({ field: 'companyName', value: address.companyName ?? '' }),
      );
      dispatch(
        orderActions.updateUserAddressField({ field: 'subpremise', value: address.addressLine2 ?? '' }),
      );

      if (!address.placeId) {
        dispatch(orderActions.autocompleteAddress({ address: addressToValidate }));
      }
    },
  });

  startListening({
    matcher: isAnyOf(updateOrder.fulfilled, userActions.toggleBypassBusinessRules),
    effect: (_, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const bypassBusinessRules = selectBypassBusinessRules(state);

      // Do not get date slots if user has bypassBusinessRules
      if (bypassBusinessRules) {
        return;
      }

      const orderId = state.order.id;
      if (orderId) {
        dispatch(getOrderDates({ orderId }));
      }
    },
  });

  startListening({
    actionCreator: userActions.updateUserLocation,
    effect: (_, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const validAddress = selectDeliveryAddressValid(state);

      if (validAddress) {
        dispatch(validateDeliveryRange());
      }
    },
  });

  startListening({
    predicate: (action) =>
      action.type === autocompleteAddress.fulfilled.type &&
      (action as ReturnType<(typeof autocompleteAddress)['fulfilled']>).payload.results.length > 0,
    effect: (action, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const { address, results } = (action as ReturnType<(typeof autocompleteAddress)['fulfilled']>).payload;
      let placeIndex = 0;
      if (results.length > 1) {
        placeIndex = results.findIndex((result) => {
          const arr = result.structuredFormatting.secondaryText.toLowerCase().split(', ');
          return arr.some(
            (e) =>
              e ===
              `${state.order?.validatedDeliveryAddress.administrativeAreaLevelOne?.toLowerCase()} ${state.order?.validatedDeliveryAddress.postalCode?.toLowerCase()}`,
          );
        });
      }
      const autocompletedAddress = results[placeIndex === -1 ? 0 : placeIndex];
      const { structuredFormatting } = autocompletedAddress;
      const { mainText } = structuredFormatting;
      const apartment = selectApartmentSuite(state);
      const mainTextWithApartment = `${mainText} ${apartment}`;
      const autocompleteResult = {
        ...autocompletedAddress,
        structuredFormatting: {
          ...structuredFormatting,
          mainText: mainTextWithApartment,
        },
      };

      dispatch(validateAddress({ address, autocompleteResult }));
    },
  });

  startListening({
    actionCreator: submitOrder.pending,
    effect: (_, listenerApi) => {
      const { getState } = listenerApi;
      const state = getState() as State;
      const analytics = createSubmitOrderAnalytics(state);
      logEvent('order/submitOrder', {
        ...analytics,
        pickupMinNotMet: undefined, // doesn't seem to be accurate at this point
      });
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (action, listenerApi) => {
      const { getState } = listenerApi;
      const state = getState() as State;

      const analytics = createSubmitOrderAnalytics(state);
      const { payload } = action;
      logEvent('order/submitOrderSuccess', {
        ...analytics,
        orderId: payload.previousOrderState.id,
        taxAmount: payload.previousOrderState.taxAmount,
        subTotalAmount: payload.previousOrderState.subTotalAmount,
        reorder: payload.previousOrderState.reorder,
        totalAmount: roundNumber(
          (payload?.previousOrderState?.taxAmount || 0) +
            (payload?.previousOrderState?.subTotalAmount || 0) +
            (payload?.previousOrderState?.deliveryTip.tipAmount || 0),
        ),
        clientId: payload.order.clientId,
        isEditingOrder: undefined,
        destination: undefined,
        promoFreeItemCount: undefined,
        promoFreeItemsValue: undefined,
        sendCustomerNotifications: undefined,
      });
    },
  });

  startListening({
    actionCreator: submitOrder.rejected,
    effect: (_, listenerApi) => {
      const { getState } = listenerApi;
      const state = getState() as State;
      const analytics = createSubmitOrderAnalytics(state);

      logEvent('order/submitOrderFailure', {
        ...analytics,
        taxAmount: undefined,
        subTotalAmount: undefined,
        totalAmount: undefined,
        reorder: undefined,
        pickupMinNotMet: undefined,
        isEditingOrder: undefined,
        promoFreeItemCount: undefined,
        promoFreeItemsValue: undefined,
      });
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (action, listenerApi) => {
      // we must use the original state given that we clear state after submitting an order
      const { getOriginalState } = listenerApi;
      const state = getOriginalState() as State;

      const { previousOrderState } = action.payload;
      const guestName = selectFullName(state);
      const isCmtCustomer = selectIsCmtCustomerSelected(state);

      history.push({
        pathname: '/submit',
        state: {
          order: previousOrderState,
          guestName,
          isCmtCustomer,
        },
      });
    },
  });

  startListening({
    matcher: isAnyOf(submitOrder.fulfilled, orderActions.exitEditOrder),
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(orderActions.resetDetailsForm());
      dispatch(orderActions.resetSecondaryContactForm());
    },
  });

  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action, { dispatch }) => {
      const { guest = {}, order = {}, reorder = false } = action.payload;
      history.push({ pathname: reorder ? '/event' : '/' });

      const { first, last, phone, email } = guest as GuestDetails;
      const { firstName, lastName } = guest as GuestSearchResult;
      const {
        paperGoods,
        guestCount,
        specialInstructions,
        promiseDateTime,
        payment,
        status,
        deliveryAddress,
        destination,
        secondaryContact,
        cateringReason,
        sendCustomerNotifications,
      } = order as Order;
      const showSpecialInstructions = reorder
        ? stripPaperGoodsFromSpecialInstructions(specialInstructions ?? '')
        : specialInstructions;
      const { paymentType } = payment || {};

      let selectedPaymentMethod;
      if (status === ooeConstants.PAYMENT_PENDING) {
        selectedPaymentMethod = ooeConstants.REQUEST_PAYMENT;
      } else if (paymentType === ooeConstants.GET_PAYMENT_TYPES.ACCOUNT) {
        selectedPaymentMethod = ooeConstants.CREDIT;
      } else if (paymentType === ooeConstants.GET_PAYMENT_TYPES.TO_BE_COLLECTED) {
        selectedPaymentMethod = ooeConstants.DEFER;
      }

      let date = null;
      let time = null;
      if (!reorder && promiseDateTime) {
        const parsedDateTime = parseISO(promiseDateTime);
        date = startOfDay(parsedDateTime);
        time = format(parsedDateTime, ooeConstants.DATE_TIME_FORMAT.time);
      }

      dispatch(orderActions.resetPaymentMethod());

      dispatch(
        guestActions.initializeGuestForm({
          cfaId: (guest as GuestDetails).cfaId,
          first: first || firstName || '',
          last: last || lastName || '',
          email: email ?? '',
          phone: phone ?? '',
        }),
      );

      // Update details form values
      dispatch(
        orderActions.initializeDetailsForm({
          date,
          time,
          paperGoods,
          guestCount: guestCount != null ? String(guestCount) : undefined,
          specialInstructions: showSpecialInstructions,
          cateringReason,
        }),
      );

      // Update secondary contact form values
      if (secondaryContact) {
        dispatch(
          orderActions.initializeSecondaryContactForm({
            firstName: secondaryContact?.firstName,
            lastName: secondaryContact?.lastName,
            phoneNumber: secondaryContact?.phoneNumber,
          }),
        );
      }

      // Select correct payment method
      if (!reorder) {
        dispatch(orderActions.initializePaymentMethod(selectedPaymentMethod));
      }

      // Only validate address if destination is Delivery and a delivery Address is available
      if (destination === ooeConstants.DELIVERY && deliveryAddress) {
        const { addressLine1, addressLine2, city, state, placeId } = deliveryAddress;

        if (getVariation('showDeliveryAddressSearchBar', true)) {
          const { mainText, secondaryText, searchValue, addressToValidate } =
            getAddressSearchSeed(deliveryAddress);
          dispatch(orderActions.updateUserAddressField({ field: 'subpremise', value: addressLine2 ?? '' }));
          if (placeId) {
            dispatch(
              eventDetailsActions.selectAddressSearchResult({
                placeId,
                structuredFormatting: { mainText, secondaryText },
              }),
            );
          } else {
            dispatch(eventDetailsActions.seedAddressSearchFromEdit({ value: searchValue, placeId: null }));
            dispatch(orderActions.autocompleteAddress({ address: addressToValidate }));
          }
        } else {
          // Keep Unit out of the Google query; it is applied via subpremise on validate.
          dispatch(orderActions.autocompleteAddress({ address: `${addressLine1} ${city} ${state}` }));
        }
      }

      // for re-orders, reset consent to force users to have to select yes/no
      dispatch(orderActions.setSmsConsent({ smsConsent: reorder ? null : !!sendCustomerNotifications }));
    },
  });
};

export default addOrderListeners;
